import React, { useState, Fragment, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import InputMask from "react-input-mask";
import {
  Grid,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
  Box,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuoteTwoTone";
import * as Yup from "yup";
import moment from "moment";
import { debounce } from "lodash";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { useCalculateApplication } from "@/hooks/useCalculateApplication";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { getAllLenders, lenderCardsSelector } from "@/store/slices/lenderCards";
import { getFilteredLenders } from "@/utils/commonFunctions";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import { useDemoCommercialApplication } from "@/hooks";
import { useFeatureFlags } from "../../utils/featureFlags";
import {
  getLoanPurposeTypes,
  getIsAssetLoan,
  APPLICATION_STATUS_LABEL,
  APPLICATION_SECTIONS_ID,
} from "../../constants";
import regex from "../../utils/regex";
import { NumericFormatCustom, currencyMaskedValue } from "../../utils/currencyMaskFormat";
import {
  loanDetailsSelector,
  updateLoanDetails,
  saveLoanDetails,
  assetSelector,
  saveAsset,
  updateAsset,
  userDetailsSelector,
  applicationSelector,
  updateApplicationField,
  saveValue,
} from "../../store/slices/applicationFormSlice";
import { isValidInput } from "../Utils/helperFunction";
import { userPermissionsSelector } from "../../store/slices/userSlice";
import FormDateInput from "../CommonComponents/FormDateInput";
import FormInputField from "../CommonComponents/FormInputField";
import { PercentageFormatCustom } from "../../utils/percentageMaskFormat";
import RadioButtonGroup from "../Utils/RadioButtonGroup";
import GoogleAddressSearch from "../Utils/GoogleAddressSearchRegister";

const dollarStringOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
};

const LoanDetails = React.memo(({ applicationType, isDisableEdit }) => {
  const dispatch = useDispatch();
  const permissions = useSelector(userPermissionsSelector);
  const { accessComponents } = useAccessAppComponent();
  const { isDemoCommercialApplication } = useDemoCommercialApplication();
  const {
    application: {
      loanDetails: { isOriginationFeeEnabled },
    },
  } = accessComponents;
  const { lenders } = useSelector(lenderCardsSelector);
  const {
    getBrokeragePercentage,
    getBrokerageAmount,
    getBalloonPercentage,
    getBalloonAmount,
    getNetAssetValueProvidedSingleField,
  } = useCalculateApplication();
  const { isCommissionEnabled } = useFeatureFlags();
  const { openManualQuote, demoLoanDetailsFields } = useSelector(userDetailsSelector);
  const {
    _id: applicationId,
    status,
    settlementRate,
    lenderApplicationReference,
    settlementDate,
    purpose: appPurpose,
  } = useSelector(applicationSelector);
  const loanPurposeTypes = getLoanPurposeTypes[applicationType] ?? [];
  const isAssetLoan = getIsAssetLoan(applicationType);
  const isConsumerApplication = applicationType === ApplicationType.Consumer;
  const loanDetails = useSelector(loanDetailsSelector);
  const asset = useSelector(assetSelector);
  const {
    _id,
    balloon = 0,
    balloonAmount = 0,
    tradeIn = null,
    payout = null,
    deposit = null,
    requestedLoanAmount = null,
    purpose,
    term,
    commission,
    brokerage,
    brokerageAmount,
    originationFee,
    rateAdjustment = 0,
    lender,
  } = loanDetails;

  const { ageOfAsset = "", assetValue } = asset; // purpose ageOfAssetAtEnd

  const rawLenderList =
    isCommissionEnabled() && lenders?.length > 0
      ? lenders?.map((lender) => {
        const { lender: lenderName } = lender;
        return lenderName;
      })
      : [];

  const lenderList =
    rawLenderList?.length > 0 && permissions?.lenders
      ? getFilteredLenders({
        applicationType: applicationType,
        permittedLenders: permissions,
        lenderList: rawLenderList,
        isSorted: true,
      })
      : [];

  const [fieldErrors, setFieldErrors] = useState({
    lender: "",
    commission: "",
    deposit: "",
    requestedLoanAmount: "",
    tradeIn: "",
    payout: "",
    term: "",
    assetValue: "",
    purpose: "",
    originationFee: "",
    brokerage: "",
    brokerageAmount: "",
    settlementDate: "",
    lenderApplicationReference: "",
    settlementRate: "",
  });

  const validationSchema = Yup.object({
    deposit: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    requestedLoanAmount: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    tradeIn: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    payout: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    assetValue: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    originationFee: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    commission: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be to up 7 digit and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    purpose: Yup.string().optional(),
    // brokerage: Yup.string()
    //   .optional()
    //   .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
    //   .matches(regex.allowNumberAndDecimal, {
    //     message: "Number must be up to 7 digits and up to 2 decimal places",
    //   })
    //   .max(10, "Maximum of 7 digits"),
    term: Yup.string()
      .optional()
      .matches(regex.termsRegex, {
        message: "Terms must be up to 120 months",
      })
      .max(3, "Maximum of 3 digits"),
    brokerage: Yup.string()
      .optional()
      .matches(regex.percentageRegex, "Please enter valid percentage.")
      .matches(regex.percentageRegex, {
        message: "Percentage should be upto 100%.",
      }),
    brokerageAmount: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    balloon: Yup.string()
      .optional()
      .matches(regex.percentageRegex, "Please enter valid percentage.")
      .matches(regex.percentageRegex, {
        message: "Percentage should be upto 100%.",
      }),
    balloonAmount: Yup.string()
      .optional()
      .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
      .matches(regex.allowNumberAndDecimal, {
        message: "Number must be up to 7 digits and up to 2 decimal places",
      })
      .max(10, "Maximum of 7 digits"),
    lender: Yup.string().optional(),
    settlementDate: Yup.string()
      .typeError("Please enter a valid date")
      .matches(regex.customerDateOfBirthRegexOptional, "Invalid date, please enter a valid date")
      .optional()
      .max(new Date(), "Date is not select in the future"),
    lenderApplicationReference: Yup.string().optional(),
    settlementRate: Yup.string()
      .optional()
      .matches(regex.percentageRegex, "Please enter valid percentage.")
      .matches(regex.percentageRegex, {
        message: "Percentage should be upto 100%.",
      }),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        await validationSchema.validateAt(fieldName, { [fieldName]: value });
        setFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
      } catch (error) {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: error.message,
        }));
      }
    }, 300),
    [],
  );

  const [allValues, setAllValue] = useState({
    deposit: deposit || "",
    requestedLoanAmount: requestedLoanAmount || "",
    balloon: balloon || 0,
    balloonAmount: balloonAmount || 0,
    term: term,
    tradeIn: tradeIn || "",
    payout: payout || "",
    purpose: purpose,
    assetValue: assetValue || "",
    brokerage: isDemoCommercialApplication ? 1.5 : (brokerage || 0),
    commission: commission || "",
    brokerageAmount: brokerageAmount || 0,
    originationFee: originationFee || "",
    rateAdjustment: rateAdjustment || 0,
    lender: lender || "",
    settlementDate: settlementDate || "",
    lenderApplicationReference: lenderApplicationReference || "",
    settlementRate: settlementRate || "",
    // For application purpose only
    loanAmount: "",
    estabilishmentFee: isDemoCommercialApplication ? 2 : "",
    estabilishmentFeeAmount: "",
    applicantsImmediate: "",
    loanPurpose: "",
    interestOnly: "",
    loanDetailsAddress: "",
  });

  const [mode, setMode] = useState({ balloon: "percentage", brokerage: "percentage", estabilishmentFee: "percentage" });
  const isBallonPerCheck = mode.balloon === "percentage";
  const isBrokeragePerCheck = mode.brokerage === "percentage";
  const isEstabilishmentFeePerCheck = mode.estabilishmentFee === "percentage";

  const handleModeChange = (key, newMode) => {
    if (newMode !== null) {
      if (key === "brokerage") {
        if (mode.brokerage !== "percentage") {
          debouncedValidation("brokerage", allValues.brokerage);
        } else {
          debouncedValidation("brokerageAmount", allValues.brokerageAmount);
        }
      } else if (key === "balloon") {
        if (mode.balloon !== "percentage") {
          debouncedValidation("balloon", allValues.balloon);
        } else {
          debouncedValidation("balloonAmount", allValues.balloonAmount);
        }
      } else if (key === "estabilishmentFee") {
        if (mode.estabilishmentFee !== "percentage") {
          debouncedValidation("estabilishmentFee", allValues.estabilishmentFee);
        } else {
          debouncedValidation("estabilishmentFeeAmount", allValues.estabilishmentFeeAmount);
        }
      }
      // setMode(newMode);
      setMode({
        ...mode,
        [key]: newMode,
      });
    }
  };

  const handle = {
    onChangeField: async (e, name) => {
      let isValid = true;

      // if (e === "") return setAllValue({ ...allValues, [name]: e });
      if (e === "") {
        setAllValue({ ...allValues, [name]: e });

        // Condition added due to set error for terms
        if (name !== "term") {
          setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
          return;
        }
      }

      if (
        name === "deposit" ||
        name === "tradeIn" ||
        name === "payout" ||
        name === "assetValue" ||
        name === "commission" ||
        name === "originationFee"
      ) {
        isValid = isValidInput(e);
      }
      if (!isValid) {
        // await validateField(name, e);
        debouncedValidation(name, e);
        return;
      }

      if (name === "loanAmount") {
        dispatch(saveValue({ demoLoanAmount: Number(e) }));
      }
      if (name === "interestRate") {
        dispatch(saveValue({ demoInterestRate: e }));
      }
      if (name === "estimatedValue") {
        dispatch(saveValue({ demoEstimatedValue: +e }));
      }

      if (name === "brokerage") {
        if (mode.brokerage === "percentage") {
          if (e < 0) {
            debouncedValidation(name, e);
            return;
          }
          const updatedPayload = {
            ...allValues,
            [name]: e,
            brokerageAmount: isDemoCommercialApplication
              ? (e / 100) * +allValues?.loanAmount
              : getBrokerageAmount({ brokeragePercentage: Number(e) }),
          };
          setAllValue(updatedPayload);
          if (isDemoCommercialApplication) {
            dispatch(
              saveValue({
                demoLoanDetailsFields: { ...demoLoanDetailsFields, ...updatedPayload },
                demoLoanAmount:
                  updatedPayload.brokerageAmount +
                  Number(allValues.estabilishmentFeeAmount) +
                  Number(allValues?.loanAmount),
              }),
            );
          }
          debouncedValidation(name, e);
          return;
        } else {
          const updatedPayload = {
            ...allValues,
            [name]: getBrokeragePercentage({ brokerageAmount: Number(e) }),
            brokerageAmount: Math.abs(e),
          };
          setAllValue(updatedPayload);
          if (isDemoCommercialApplication) {
            dispatch(
              saveValue({
                demoLoanDetailsFields: { ...demoLoanDetailsFields, ...updatedPayload },
              }),
            );
          }
        }
      } else if (name === "balloon") {
        if (mode.balloon === "percentage") {
          if (e < 0) {
            debouncedValidation(name, e);
            return;
          }
          setAllValue({ ...allValues, [name]: +e, balloonAmount: getBalloonAmount({ balloonPercentage: Number(e) }) });
          debouncedValidation(name, e);
          return;
        } else {
          setAllValue({
            ...allValues,
            [name]: getBalloonPercentage({ balloonAmount: Number(e) }),
            balloonAmount: Math.abs(e),
          });
          // debouncedValidation(name, e);
        }
      } else if (name === "estabilishmentFee") {
        if (mode.estabilishmentFee === "percentage") {
          const updatedPayload = {
            ...allValues,
            [name]: +e,
            estabilishmentFeeAmount: handle.getEstabilishmentFeeAmount({ estabilishmentFeePercentage: Number(e) }),
          };
          setAllValue(updatedPayload);
          if (isDemoCommercialApplication) {
            dispatch(
              saveValue({
                demoLoanDetailsFields: { ...demoLoanDetailsFields, ...updatedPayload },
                demoLoanAmount:
                  updatedPayload.estabilishmentFeeAmount +
                  Number(allValues.brokerageAmount) +
                  Number(allValues?.loanAmount),
              }),
            );
          }
          return;
        } else {
          const updatedPayload = {
            ...allValues,
            [name]: handle.getEstabilishmentFeePercentage({ estabilishmentFeeAmount: Number(e) }),
            estabilishmentFeeAmount: Math.abs(e),
          };
          setAllValue(updatedPayload);
          if (isDemoCommercialApplication) {
            dispatch(
              saveValue({
                demoLoanDetailsFields: { ...demoLoanDetailsFields, ...updatedPayload },
                demoLoanAmount:
                  updatedPayload.estabilishmentFeeAmount +
                  Number(allValues.brokerageAmount) +
                  Number(allValues?.loanAmount),
              }),
            );
          }
        }
      } else {
        if (name === "term") {
          const stringValue = e.toString();
          const trimmedValue =
            stringValue.length > 1 && stringValue.startsWith("0") ? stringValue.substring(1) : stringValue;
          const numValue = trimmedValue === "" ? "" : +trimmedValue || 0;
          setAllValue({ ...allValues, [name]: numValue });
          if (isDemoCommercialApplication) {
            dispatch(
              saveValue({
                demoLoanDetailsFields: { ...demoLoanDetailsFields, ...allValues, [name]: numValue },
              }),
            );
          }
        } else {
          setAllValue({ ...allValues, [name]: e });
          if (isDemoCommercialApplication && name !== "exitStrategy") {
            // calculate brokerage & estabilishment fee
            let updatedPayload = {
              ...demoLoanDetailsFields,
              ...allValues,
              [name]: e
            }

            if (name === "loanAmount") {
              updatedPayload = {
                ...updatedPayload,
                brokerageAmount: (allValues?.brokerage / 100) * +allValues?.loanAmount,
                estabilishmentFeeAmount: handle.getEstabilishmentFeeAmount({ estabilishmentFeePercentage: Number(allValues?.estabilishmentFee) }),
              }
              setAllValue(updatedPayload);
            }
            dispatch(
              saveValue({
                demoLoanDetailsFields: { ...updatedPayload },
              }),
            );
          }
        }
        debouncedValidation(name, e);
      }
    },
    getEstabilishmentFeeAmount: ({ estabilishmentFeePercentage }) => {
      return (estabilishmentFeePercentage / 100) * Number(allValues?.loanAmount);
    },
    getEstabilishmentFeePercentage: ({ estabilishmentFeeAmount }) => {
      const estabilishmentFeePercentage = (estabilishmentFeeAmount / Number(allValues?.loanAmount)) * 100;
      return isFinite(estabilishmentFeePercentage) ? estabilishmentFeePercentage : 0;
    },
    loanDetailsFn: (value) => {
      const keys = Object.keys(value);
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (loanDetails[key] !== value[key]) {
          dispatch(saveLoanDetails(value));
          dispatch(updateLoanDetails({ applicationId, _id, ...value }));
        }
      }
    },
    updateAsset: async (value) => {
      dispatch(updateAsset({ applicationId, _id: asset._id, ...value }));
    },
    termValueFn: () => {
      if (allValues?.term === loanDetails.term) {
        return;
      }

      if (+allValues?.term > 0 && +allValues?.term <= 120) {
        const termInYears = allValues?.term / 12;
        const ageOfAssetMoment = moment(ageOfAsset, "YYYY");
        const endOfTermYear = moment().add(termInYears, "year");
        const ageOfAssetAtEnd = endOfTermYear.diff(ageOfAssetMoment, "years");
        handle.apiCallFn(ageOfAssetAtEnd);
      }
    },
    // The repayment term is entered on every application type, so it is always saved. Only the asset's
    // age at the end of the term is asset specific, so that dispatch alone is skipped for the other types.
    apiCallFn: (ageOfAssetAtEnd) => {
      Promise.all([
        dispatch(saveLoanDetails({ term: allValues?.term })),
        ...(isAssetLoan ? [dispatch(updateAsset({ applicationId, _id: asset._id, ageOfAssetAtEnd }))] : []),
        dispatch(updateLoanDetails({ applicationId, _id, term: allValues?.term })),
      ]);
    },
    blurFn: async (fieldName, value) => {
      try {
        if (fieldName === "exitStrategy") {
          dispatch(
            saveValue({
              demoLoanDetailsFields: { ...demoLoanDetailsFields, ...allValues, [fieldName]: value },
            }),
          );
        }
        if (fieldErrors[fieldName] === "") {
          // We need to update and save the net asset value when the fields that impact it are changed.
          // Impacted fields are: asset.assetValue, loanDetails.deposit, loanDetails.tradeIn, loanDetails.payout.
          if (fieldName === "deposit" || fieldName === "tradeIn" || fieldName === "payout") {
            const netAssetValue = getNetAssetValueProvidedSingleField({ fieldName, fieldValue: value });
            handle.updateAsset({
              netAssetValue,
            });
          }
          handle.loanDetailsFn({
            [fieldName]: value || 0,
          });
        }
      } catch (error) {
        const newErrors = {};
        error.inner.forEach((validationError) => {
          newErrors[validationError.path] = validationError.message;
        });
        setFieldErrors(newErrors);
      }
    },
    blurFnPersonalType: (fieldName, value) => {
      try {
        if (fieldErrors[fieldName] === "") dispatch(saveAsset({ [fieldName]: value }));
        if (_id)
          dispatch(
            updateAsset({
              applicationId,
              ...asset,
              [fieldName]: value,
            }),
          );
      } catch (error) {
        const newErrors = {};
        error.inner.forEach((validationError) => {
          newErrors[validationError.path] = validationError.message;
        });
        setFieldErrors(newErrors);
      }
    },
    onUpdateApplicationField: (fieldName, value) => {
      dispatch(updateApplicationField({ _id: applicationId, [fieldName]: value }));
    },
    handleUpdateAddress: (newAddressValue) => {
      if (newAddressValue) {
        const address = newAddressValue?.formatted_address;
        setAllValue({ ...allValues, loanDetailsAddress: address });
        dispatch(
          saveValue({
            demoLoanDetailsFields: { ...demoLoanDetailsFields, loanDetailsAddress: address },
          }),
        );
      }
    }
  };

  const { getAssetOrLoanDetailsLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldName, localFieldName, fieldError }) =>
    getAssetOrLoanDetailsLocalAndLenderError({
      fieldName,
      localFieldName,
      localState: allValues,
      fieldError,
      isLoanDetails: true,
    });

  useEffect(() => {
    if (isCommissionEnabled()) {
      dispatch(getAllLenders());
    }
  }, []);

  return (
    <Grid
      id={APPLICATION_SECTIONS_ID.LOAN_DETAILS}
      container
      style={{
        margin: "0 0 30px",
        padding: "0 0 20px",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
      }}
    >
      <Grid container item xs={12} sm={12} md={2}>
        <RequestQuoteIcon style={{ marginRight: "10px" }} />
        <Typography>Loan Details</Typography>
      </Grid>

      <Grid
        container
        item
        xs={12}
        sm={12}
        md={10}
        style={{
          padding: "0 0 0 10px",
        }}
      >
        {isAssetLoan && (
          <Grid container item style={{ margin: "0 0 10px" }}>
            <Grid container item spacing={1}>
              {isDemoCommercialApplication && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      id="loanAmount"
                      label="Loan Amount"
                      variant="filled"
                      type="text"
                      size="small"
                      name="loanAmount"
                      value={allValues?.loanAmount}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "loanAmount")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item sm={4} xs={12}>
                    <TextField
                      fullWidth
                      id="term"
                      label={`Repayment term (in months)`}
                      variant="filled"
                      type="number"
                      size="small"
                      name="term"
                      value={allValues?.term}
                      disabled={isDisableEdit}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "term")}
                      onBlur={(e) => handle.termValueFn()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      id="interestRate"
                      label="Interest Rate"
                      variant="filled"
                      type="text"
                      size="small"
                      name="interestRate"
                      value={allValues?.interestRate}
                      disabled={isDisableEdit}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "interestRate")}
                      InputProps={{
                        inputComponent: PercentageFormatCustom,
                      }}
                    />
                  </Grid>
                </>
              )}

              {!isDemoCommercialApplication && (
                <>
                  <Grid item sm={4} xs={12}>
                    <TextField
                      fullWidth
                      id="deposit"
                      label="Deposit amount"
                      variant="filled"
                      type="text"
                      size="small"
                      name="deposit"
                      disabled={isDisableEdit}
                      value={allValues?.deposit}
                      error={Boolean(getLocalAndLenderError({ fieldName: "deposit", fieldError: fieldErrors?.deposit }))}
                      helperText={getLocalAndLenderError({ fieldName: "deposit", fieldError: fieldErrors?.deposit })}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "deposit")}
                      onBlur={(e) => handle.blurFn("deposit", currencyMaskedValue(e.target.value))}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={4} sm={3}>
                    <TextField
                      fullWidth
                      id="tradeIn"
                      variant="filled"
                      type="text"
                      name="tradeIn"
                      label="Trade in amount"
                      size="small"
                      disabled={isDisableEdit}
                      value={allValues?.tradeIn}
                      error={Boolean(getLocalAndLenderError({ fieldName: "tradeIn", fieldError: fieldErrors?.tradeIn }))}
                      helperText={getLocalAndLenderError({ fieldName: "tradeIn", fieldError: fieldErrors?.tradeIn })}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                      }}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "tradeIn")}
                      onBlur={(e) => handle.blurFn("tradeIn", currencyMaskedValue(e.target.value))}
                    />
                  </Grid>
                  <Grid item md={4} sm={3}>
                    <TextField
                      fullWidth
                      id="payout"
                      variant="filled"
                      type="text"
                      name="payout"
                      label="Payout amount"
                      size="small"
                      value={allValues?.payout}
                      error={Boolean(getLocalAndLenderError({ fieldName: "payout", fieldError: fieldErrors?.payout }))}
                      helperText={getLocalAndLenderError({ fieldName: "payout", fieldError: fieldErrors?.payout })}
                      disabled={isDisableEdit}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                      }}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "payout")}
                      onBlur={(e) => handle.blurFn("payout", currencyMaskedValue(e.target.value))}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        )}

        <Grid container item xs={12} style={{ margin: "0 0 10px" }}>
          <Grid container item spacing={1} alignItems="flex-start">
            {(applicationType === ApplicationType.Personal || applicationType === ApplicationType.Business) && (
              <Fragment>
                <Grid item md={4} sm={4}>
                  <TextField
                    id="requestedLoanAmount"
                    fullWidth
                    variant="filled"
                    type="text"
                    name="requestedLoanAmount"
                    label="Requested loan amount"
                    size="small"
                    disabled={isDisableEdit}
                    value={allValues?.requestedLoanAmount}
                    error={Boolean(getLocalAndLenderError({
                      fieldName: "requestedLoanAmount",
                      fieldError: fieldErrors?.requestedLoanAmount,
                    }))}
                    helperText={getLocalAndLenderError({
                      fieldName: "requestedLoanAmount",
                      fieldError: fieldErrors?.requestedLoanAmount,
                    })}
                    onChange={(event) => handle.onChangeField(event?.target?.value, "requestedLoanAmount")}
                    onBlur={(e) => handle.blurFn("requestedLoanAmount", currencyMaskedValue(e.target.value))}
                    InputProps={{
                      inputComponent: NumericFormatCustom,
                      style: {
                        borderTopRightRadius: 0,
                      },
                    }}
                  />
                </Grid>

                <Grid item md={4} sm={4}>
                  <TextField
                    fullWidth
                    variant="filled"
                    select
                    value={allValues?.purpose}
                    label="Loan purpose"
                    size="small"
                    name="purpose"
                    disabled={isDisableEdit}
                    onChange={(event) => handle.onChangeField(event?.target?.value, "purpose")}
                    onBlur={(e) => handle.blurFn("purpose", e.target.value)}
                    error={Boolean(getLocalAndLenderError({ fieldName: "purpose", fieldError: fieldErrors?.purpose }))}
                    helperText={getLocalAndLenderError({ fieldName: "purpose", fieldError: fieldErrors?.purpose })}
                  >
                    {loanPurposeTypes.map((purpose) => (
                      <MenuItem value={purpose} key={purpose}>
                        {purpose}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Fragment>
            )}

            {applicationType === ApplicationType.Consumer && !openManualQuote && (
              <Fragment>
                <Grid item sm={4} xs={12}>
                  <Stack direction="column">
                    <Typography variant="caption">Rate adjustment {rateAdjustment}%</Typography>
                    <Box style={{ padding: "0 7px" }}>
                      <Slider
                        color="secondary"
                        size="large"
                        marks={[
                          { value: -2, label: "-2" },
                          { value: -1.5, label: "-1.5" },
                          { value: -1, label: "-1" },
                          { value: -0.5, label: "-0.5" },
                          { value: 0, label: "0" },
                          // { value: 0.5, label: "0.5" },
                          // { value: 1, label: "1" },
                          // { value: 1.5, label: "1.5" },
                          // { value: 2, label: "2" },
                        ]}
                        step={0.5}
                        min={-2}
                        max={0}
                        value={allValues?.rateAdjustment}
                        name="rateAdjustment"
                        onChange={(event) => handle.onChangeField(event?.target?.value, "rateAdjustment")}
                        onChangeCommitted={() =>
                          handle.loanDetailsFn({
                            rateAdjustment: allValues?.rateAdjustment,
                          })
                        }
                        aria-label="Default"
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Stack>
                </Grid>
              </Fragment>
            )}

            {!isDemoCommercialApplication && (
              <Grid item sm={isDemoCommercialApplication ? 3 : 4} xs={12}>
                <Stack direction="column">
                  {isAssetLoan && (
                    <Typography variant="caption" sx={{ m: 0, p: 0 }}>
                      Repayment term (in months)
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    id="term"
                    label={`Repayment term ${isAssetLoan ? "" : "(in months)"}`}
                    variant="filled"
                    type="number"
                    size="small"
                    name="term"
                    disabled={isDisableEdit}
                    value={allValues?.term}
                    error={Boolean(getLocalAndLenderError({ fieldName: "term", fieldError: fieldErrors?.term }))}
                    helperText={getLocalAndLenderError({ fieldName: "term", fieldError: fieldErrors?.term })}
                    onChange={(event) => handle.onChangeField(event?.target?.value, "term")}
                    onBlur={(e) => handle.termValueFn()}
                  />
                </Stack>
              </Grid>
            )}

            {!isDemoCommercialApplication && isAssetLoan && (
              <Fragment>
                <Grid item sm={4} xs={12}>
                  <Stack direction="column">
                    <Typography variant="caption">
                      Balloon {!isNaN(allValues?.balloon) ? Math.round(allValues?.balloon * 100) / 100 : 0}% |{" "}
                      {(allValues?.balloonAmount ? allValues?.balloonAmount : 0).toLocaleString(
                        "en-US",
                        dollarStringOptions,
                      )}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <TextField
                        label="Balloon"
                        name="balloon"
                        disabled={isDisableEdit}
                        value={isBallonPerCheck ? allValues?.balloon : allValues?.balloonAmount}
                        error={Boolean(isBallonPerCheck ? fieldErrors?.balloon : fieldErrors?.balloonAmount)}
                        helperText={isBallonPerCheck ? fieldErrors?.balloon : fieldErrors?.balloonAmount}
                        onChange={(event) => {
                          const value = event.target.value;
                          // Handle clearing the value properly
                          handle.onChangeField(value || 0, "balloon");
                        }}
                        onBlur={() => {
                          handle.loanDetailsFn({
                            balloon: +allValues?.balloon || 0,
                            balloonAmount: +allValues?.balloonAmount || 0,
                          });
                        }}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          inputProps: {
                            prefix: "",
                            isAllowedFlag: true,
                            maxDigits: 7,
                            maxPlaces: 2,
                          },
                          startAdornment: allValues?.balloonAmount > 0 && mode.balloon !== "percentage" && (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <ToggleButtonGroup
                                value={mode.balloon}
                                exclusive
                                color="primary"
                                onChange={(event, value) => handleModeChange("balloon", value)}
                                sx={{
                                  mr: -1,
                                  height: "36px",
                                }}
                              >
                                <ToggleButton value="percentage">%</ToggleButton>
                                <ToggleButton value="dollar">$</ToggleButton>
                              </ToggleButtonGroup>
                            </InputAdornment>
                          ),
                        }}
                        type="text"
                        size="small"
                        variant="filled"
                        fullWidth
                      />
                    </Box>
                  </Stack>
                </Grid>
              </Fragment>
            )}
            {isDemoCommercialApplication && (
              <Grid item xs={12} sm={4}>
                <InputMask
                  mask="99-99-9999"
                  value={allValues?.loanSettlementDate || ""}
                  disabled={isDisableEdit}
                  maskChar={null}
                  onChange={(event) => handle.onChangeField(event?.target?.value, "loanSettlementDate")}
                >
                  {() => (
                    <TextField
                      label="Loan Settlement Date"
                      size="small"
                      fullWidth
                      placeholder="DD-MM-YYYY"
                      variant="filled"
                      disabled={isDisableEdit}
                      name="loanSettlementDate"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </InputMask>
              </Grid>
            )}

            {applicationType === ApplicationType.Commercial && !openManualQuote && (
              <Fragment>
                <Grid item sm={4} xs={12}>
                  <Stack direction="column">
                    {!isDemoCommercialApplication && (
                      <Typography variant="caption">
                        Brokerage {!isNaN(allValues?.brokerage) ? Math.round(allValues?.brokerage * 100) / 100 : 0}% |{" "}
                        {(allValues?.brokerageAmount ? allValues?.brokerageAmount : 0).toLocaleString(
                          "en-US",
                          dollarStringOptions,
                        )}
                      </Typography>
                    )}
                    <Box display="flex" alignItems="center">
                      <TextField
                        label="Brokerage"
                        name="brokerage"
                        disabled={isDisableEdit}
                        value={isBrokeragePerCheck ? allValues?.brokerage : allValues?.brokerageAmount}
                        error={Boolean(isBrokeragePerCheck ? fieldErrors?.brokerage : fieldErrors?.brokerageAmount)}
                        helperText={isBrokeragePerCheck ? fieldErrors?.brokerage : fieldErrors?.brokerageAmount}
                        onChange={(event) => {
                          const value = event.target.value;
                          // Handle clearing the value properly
                          handle.onChangeField(value || 0, "brokerage");
                        }}
                        onBlur={() => {
                          if (!isDemoCommercialApplication) {
                            handle.loanDetailsFn({
                              brokerage: allValues?.brokerage || 0,
                              brokerageAmount: allValues?.brokerageAmount || 0,
                            });
                          }
                        }}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          inputProps: {
                            prefix: "",
                            isAllowedFlag: true,
                            maxDigits: 7,
                            maxPlaces: 2,
                          },
                          startAdornment: allValues?.brokerageAmount > 0 && mode.brokerage !== "percentage" && (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <ToggleButtonGroup
                                value={mode.brokerage}
                                exclusive
                                color="primary"
                                onChange={(event, value) => handleModeChange("brokerage", value)}
                                sx={{
                                  mr: -1,
                                  height: "36px",
                                }}
                              >
                                <ToggleButton value="percentage">%</ToggleButton>
                                {!isDemoCommercialApplication && <ToggleButton value="dollar">$</ToggleButton>}
                              </ToggleButtonGroup>
                            </InputAdornment>
                          ),
                        }}
                        type="text"
                        size="small"
                        variant="filled"
                        fullWidth
                      />
                    </Box>
                  </Stack>
                </Grid>
              </Fragment>
            )}

            {isDemoCommercialApplication && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Estabilishment Fee"
                    name="estabilishmentFee"
                    disabled={isDisableEdit}
                    value={
                      isEstabilishmentFeePerCheck ? allValues?.estabilishmentFee : allValues?.estabilishmentFeeAmount
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      // Handle clearing the value properly
                      handle.onChangeField(value || 0, "estabilishmentFee");
                    }}
                    InputProps={{
                      inputComponent: NumericFormatCustom,
                      inputProps: {
                        prefix: "",
                        isAllowedFlag: true,
                        maxDigits: 7,
                        maxPlaces: 2,
                      },
                      startAdornment: mode.estabilishmentFee !== "percentage" && (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <ToggleButtonGroup
                            value={mode.estabilishmentFee}
                            exclusive
                            color="primary"
                            onChange={(event, value) => handleModeChange("estabilishmentFee", value)}
                            sx={{
                              mr: -1,
                              height: "36px",
                            }}
                          >
                            <ToggleButton value="percentage">%</ToggleButton>
                            <ToggleButton value="dollar">$</ToggleButton>
                          </ToggleButtonGroup>
                        </InputAdornment>
                      ),
                    }}
                    type="text"
                    size="small"
                    variant="filled"
                    fullWidth
                  />
                </Grid>

                {/* <Grid item xs={12} sm={3}>
                  <RadioGroup
                    aria-labelledby="demo-interest-buttons-group-label"
                    name="interest-radio-buttons-group"
                    sx={{ mt: 2.5 }}
                  >
                    <FormControlLabel
                      value="interestOnly"
                      control={
                        <Checkbox
                          checked={allValues?.interestOnly || false}
                          onChange={(event) => handle.onChangeField(event?.target?.checked, "interestOnly")}
                        />
                      }
                      label="Interest Only"
                    />
                  </RadioGroup>
                </Grid> */}
              </>
            )}
          </Grid>
        </Grid>

        <Grid container item spacing={1} alignItems="flex-end">
          {!openManualQuote && (
            <>
              {applicationType === ApplicationType.Business && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption">
                    Brokerage {!isNaN(allValues?.brokerage) ? Math.round(allValues?.brokerage * 100) / 100 : 0}% |{" "}
                    {(allValues?.brokerageAmount ? allValues?.brokerageAmount : 0).toLocaleString(
                      "en-US",
                      dollarStringOptions,
                    )}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <TextField
                      label="Brokerage"
                      name="brokerage"
                      disabled={isDisableEdit}
                      value={isBrokeragePerCheck ? allValues?.brokerage : allValues?.brokerageAmount}
                      error={Boolean(isBrokeragePerCheck ? fieldErrors?.brokerage : fieldErrors?.brokerageAmount)}
                      helperText={isBrokeragePerCheck ? fieldErrors?.brokerage : fieldErrors?.brokerageAmount}
                      onChange={(event) => {
                        const value = event.target.value;
                        // Handle clearing the value properly
                        handle.onChangeField(value || 0, "brokerage");
                      }}
                      onBlur={() => {
                        handle.loanDetailsFn({
                          brokerage: allValues?.brokerage || 0,
                          brokerageAmount: allValues?.brokerageAmount || 0,
                        });
                      }}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        inputProps: {
                          prefix: "",
                          isAllowedFlag: true,
                          maxDigits: 7,
                          maxPlaces: 2,
                        },
                        startAdornment: allValues?.brokerageAmount > 0 && mode.brokerage !== "percentage" && (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <ToggleButtonGroup
                              value={mode.brokerage}
                              exclusive
                              color="primary"
                              onChange={(event, value) => handleModeChange("brokerage", value)}
                              sx={{
                                mr: -1,
                                height: "36px",
                              }}
                            >
                              <ToggleButton value="percentage">%</ToggleButton>
                              <ToggleButton value="dollar">$</ToggleButton>
                            </ToggleButtonGroup>
                          </InputAdornment>
                        ),
                      }}
                      type="text"
                      size="small"
                      variant="filled"
                      fullWidth
                    />
                  </Box>
                </Grid>
              )}
              {!isDemoCommercialApplication && isOriginationFeeEnabled && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    id="originationFee"
                    label="Origination Fee"
                    variant="filled"
                    type="text"
                    size="small"
                    name="originationFee"
                    value={allValues?.originationFee}
                    disabled={isDisableEdit}
                    error={Boolean(getLocalAndLenderError({
                      fieldName: "originationFee",
                      fieldError: fieldErrors?.originationFee,
                    }))}
                    helperText={getLocalAndLenderError({
                      fieldName: "originationFee",
                      fieldError: fieldErrors?.originationFee,
                    })}
                    onChange={(event) => handle.onChangeField(event?.target?.value, "originationFee")}
                    onBlur={(e) => handle.blurFn("originationFee", currencyMaskedValue(e.target.value))}
                    InputProps={{
                      inputComponent: NumericFormatCustom,
                      style: {
                        borderTopRightRadius: 0,
                      },
                    }}
                  />
                </Grid>
              )}
            </>
          )}

          {!isDemoCommercialApplication && isCommissionEnabled() && APPLICATION_STATUS_LABEL.Settled === status && (
            <>
              {isConsumerApplication && (
                <Grid item xs={4} sm={4}>
                  <TextField
                    fullWidth
                    id="commission"
                    label="Commission Amount"
                    variant="filled"
                    type="text"
                    size="small"
                    name="commission"
                    value={allValues?.commission}
                    disabled={isDisableEdit}
                    error={Boolean(getLocalAndLenderError({
                      fieldName: "commission",
                      fieldError: fieldErrors?.commission,
                    }))}
                    helperText={getLocalAndLenderError({
                      fieldName: "commission",
                      fieldError: fieldErrors?.commission,
                    })}
                    onChange={(event) => handle.onChangeField(event?.target?.value, "commission")}
                    onBlur={(e) => handle.blurFn("commission", Number(e.target.value))}
                  />
                </Grid>
              )}
              <Grid item xs={4} sm={4}>
                <Autocomplete
                  disableClearable
                  value={allValues?.lender || ""}
                  options={lenderList}
                  name="lender"
                  onChange={(event, value) => {
                    handle.onChangeField(value ?? "", "lender");
                  }}
                  onBlur={(event) => {
                    handle.blurFn("lender", event?.target?.value);
                  }}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} size="small" fullWidth name="lender" label="Lender" variant="filled" disabled={isDisableEdit} />
                  )}
                  sx={{ flex: "2 1 auto" }}
                />
              </Grid>

              <FormDateInput
                fieldName="settlementDate"
                label="Settled Date"
                value={allValues?.settlementDate || ""}
                error={Boolean(getLocalAndLenderError({
                  fieldName: "settlementDate",
                  fieldError: fieldErrors?.settlementDate,
                }))}
                disabled={isDisableEdit}
                onChange={handle.onChangeField}
                onBlur={handle.onUpdateApplicationField}
                gridProps={{ md: 4, sm: 4, xs: 12 }}
              />

              <FormInputField
                fieldName="lenderApplicationReference"
                label="Lender Application Reference"
                value={allValues?.lenderApplicationReference || ""}
                error={Boolean(getLocalAndLenderError({
                  fieldName: "lenderApplicationReference",
                  fieldError: fieldErrors?.lenderApplicationReference,
                }))}
                disabled={isDisableEdit}
                onChange={handle.onChangeField}
                onBlur={handle.onUpdateApplicationField}
                gridProps={{ md: 4, sm: 4, xs: 12 }}
                variant="filled"
              />

              <FormInputField
                fieldName="settlementRate"
                label="Lender Submission Rate"
                value={allValues?.settlementRate || ""}
                error={Boolean(getLocalAndLenderError({
                  fieldName: "settlementRate",
                  fieldError: fieldErrors?.settlementRate,
                }))}
                onChange={handle.onChangeField}
                onBlur={handle.onUpdateApplicationField}
                gridProps={{ md: 4, sm: 4, xs: 12 }}
                variant="filled"
                type="number"
                placeholder="0.00"
                disabled={isDisableEdit}
                inputProps={{
                  inputComponent: NumericFormatCustom,
                  inputProps: {
                    prefix: "",
                    isAllowedFlag: true,
                    maxDigits: 7,
                    maxPlaces: 2,
                  },
                }}
              />
            </>
          )}

          {/* For Demo purpose only */}
          {isDemoCommercialApplication && (
            <>
              {/* Interest Only */}
              <Grid container item xs={12} sm={12} direction={"row"} my={2}>
                <Grid item xs={12} sm={8}>
                  <Typography variant="subtitle2">Applicants Immediate</Typography>
                  <RadioButtonGroup
                    value={allValues?.applicantsImmediate}
                    name={"applicantsImmediate"}
                    handleValue={(value) => {
                      handle.onChangeField(value, "applicantsImmediate");
                    }}
                    style={{
                      margin: "0 0 0 8px",
                      flexWrap: "unset",
                    }}
                    disabled={isDisableEdit}
                    options={[
                      { icon: "House", label: "Commercial" },
                      { icon: "HomeWork", label: "Residential" },
                      { icon: "Hotel", label: "Land" },
                      { icon: "House", label: "Development" },
                      { icon: "House", label: "Other" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2">Interest Only</Typography>
                  <RadioButtonGroup
                    value={allValues?.interestOnly}
                    name={"interestOnly"}
                    handleValue={(value) => {
                      handle.onChangeField(value, "interestOnly");
                    }}
                    style={{
                      margin: "0 0 0 8px",
                      flexWrap: "unset",
                    }}
                    // optionStyle={{
                    //   padding: 10,
                    // }}
                    disabled={isDisableEdit}
                    options={[
                      { icon: "House", label: "Yes" },
                      { icon: "HomeWork", label: "No" },
                    ]}
                  />
                </Grid>

                {["Other"].includes(allValues?.applicantsImmediate) && (
                  <Grid container item spacing={1} my={1}>
                    <Grid item sm={12}>
                      <TextField
                        fullWidth
                        id="otherDetails"
                        label="Other Details"
                        variant="filled"
                        type="text"
                        size="small"
                        name="otherDetails"
                        disabled={isDisableEdit}
                        value={allValues?.otherDetails}
                        onChange={(event) => handle.onChangeField(event?.target?.value, "otherDetails")}
                      />
                    </Grid>
                  </Grid>
                )}

                <Grid container item spacing={1} my={1}>
                  <Grid item sm={12}>
                    <TextField
                      fullWidth
                      id="exitStrategy"
                      label="Exit Strategy"
                      variant="filled"
                      type="text"
                      size="small"
                      name="exitStrategy"
                      multiline
                      minRows={3}
                      maxRows={3}
                      disabled={isDisableEdit}
                      value={allValues?.exitStrategy}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "exitStrategy")}
                      onBlur={(event) => handle.blurFn("exitStrategy", allValues?.exitStrategy)}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Loan Purpose */}
              <Grid item xs={12} sm={12}>
                <Typography variant="subtitle2">Loan Purpose</Typography>
                <RadioButtonGroup
                  value={allValues?.loanPurpose}
                  name={"loanPurpose"}
                  disabled={isDisableEdit}
                  handleValue={(value) => {
                    handle.onChangeField(value, "loanPurpose");
                  }}
                  style={{
                    margin: "0 0 0 8px",
                    flexWrap: "unset",
                  }}
                  options={[
                    { icon: "House", label: "New Purchase" },
                    { icon: "HomeWork", label: "Refinance" },
                    { icon: "Hotel", label: "Bridging Finance" },
                    { icon: "House", label: "Mezzanine" },
                    { icon: "House", label: "Investment" },
                    { icon: "House", label: "Renovation" },
                    { icon: "House", label: "Construction" },
                    { icon: "House", label: "Other" },
                  ]}
                />
              </Grid>

              <Grid item xs={12} sm={12} mt={2}>
                <GoogleAddressSearch
                  size="small"
                  handleCreateAddress={handle.handleUpdateAddress}
                />
              </Grid>

              {/* New Purchase */}
              {["New Purchase"].includes(allValues?.loanPurpose) && (
                <Grid container item spacing={1} my={1}>
                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="purchasePrice"
                      label="Purchase Price"
                      variant="filled"
                      type="text"
                      size="small"
                      name="purchasePrice"
                      disabled={isDisableEdit}
                      value={allValues?.purchasePrice}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "purchasePrice")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="estimatedValue"
                      variant="filled"
                      type="text"
                      name="estimatedValue"
                      label="Estimated Value"
                      size="small"
                      disabled={isDisableEdit}
                      value={allValues?.estimatedValue}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "estimatedValue")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                      }}
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="depositPaid"
                      variant="filled"
                      type="text"
                      name="depositPaid"
                      label="Deposit Paid"
                      size="small"
                      disabled={isDisableEdit}
                      value={allValues?.depositPaid}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "depositPaid")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                      }}
                    />
                  </Grid>
                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="ownFunds"
                      variant="filled"
                      type="text"
                      name="ownFunds"
                      label="Own Funds"
                      size="small"
                      disabled={isDisableEdit}
                      value={allValues?.ownFunds}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "ownFunds")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Refinance & Bridging Finance */}
              {["Refinance", "Bridging Finance"].includes(allValues?.loanPurpose) && (
                <Grid container item spacing={1} my={1}>
                  <Grid item sm={4}>
                    <TextField
                      fullWidth
                      id="currentLender"
                      label="Current Lender"
                      variant="filled"
                      type="text"
                      size="small"
                      name="currentLender"
                      disabled={isDisableEdit}
                      value={allValues?.currentLender}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "currentLender")}
                    />
                  </Grid>

                  <Grid item sm={4}>
                    <TextField
                      fullWidth
                      id="payoutFigure"
                      label="Payout Figure"
                      variant="filled"
                      type="text"
                      size="small"
                      name="payoutFigure"
                      disabled={isDisableEdit}
                      value={allValues?.payoutFigure}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "payoutFigure")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={4}>
                    <TextField
                      fullWidth
                      id="balance"
                      label="Balance"
                      variant="filled"
                      type="text"
                      size="small"
                      name="balance"
                      disabled={isDisableEdit}
                      value={allValues?.balance}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "balance")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <TextField
                      fullWidth
                      id="balanceIsFor"
                      label="Balance Is For"
                      variant="filled"
                      type="text"
                      size="small"
                      name="balanceIsFor"
                      disabled={isDisableEdit}
                      value={allValues?.balanceIsFor}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "balanceIsFor")}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Renovation & Construction */}
              {["Renovation", "Construction"].includes(allValues?.loanPurpose) && (
                <Grid container item spacing={1} my={1}>
                  <Grid item sm={12}>
                    <TextField
                      fullWidth
                      id="projectSummary"
                      label="Project Summary"
                      variant="filled"
                      type="text"
                      size="small"
                      name="projectSummary"
                      disabled={isDisableEdit}
                      value={allValues?.projectSummary}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "projectSummary")}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="buildCostRemaining"
                      label="Build Cost Remaining"
                      variant="filled"
                      type="text"
                      size="small"
                      name="buildCostRemaining"
                      disabled={isDisableEdit}
                      value={allValues?.buildCostRemaining}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "buildCostRemaining")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="councilFees"
                      label="Council Fees"
                      variant="filled"
                      type="text"
                      size="small"
                      name="councilFees"
                      disabled={isDisableEdit}
                      value={allValues?.councilFees}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "councilFees")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="otherCharges"
                      label="Other Infrastructure Charges"
                      variant="filled"
                      type="text"
                      size="small"
                      name="otherCharges"
                      disabled={isDisableEdit}
                      value={allValues?.otherCharges}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "otherCharges")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="currentDebt"
                      label="Current Debt"
                      variant="filled"
                      type="text"
                      size="small"
                      name="currentDebt"
                      disabled={isDisableEdit}
                      value={allValues?.currentDebt}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "currentDebt")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="valuationAsIs"
                      label="Valuation “As Is”"
                      variant="filled"
                      type="text"
                      size="small"
                      name="valuationAsIs"
                      disabled={isDisableEdit}
                      value={allValues?.valuationAsIs}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "valuationAsIs")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={3}>
                    <TextField
                      fullWidth
                      id="valuationAsComplete"
                      label="Valuation “As Complete”"
                      variant="filled"
                      type="text"
                      size="small"
                      name="valuationAsComplete"
                      disabled={isDisableEdit}
                      value={allValues?.valuationAsComplete}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "valuationAsComplete")}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        style: {
                          borderTopRightRadius: 0,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item sm={12}>
                    <RadioGroup
                      aria-labelledby="constructionType-check-buttons-group-label"
                      name="constructionType-check-buttons-group"
                      value={allValues?.constructionType}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "constructionType")}
                    >
                      <FormControlLabel value="isPlaceApproved" control={<Checkbox />} label="Approvals in Place" />
                      <FormControlLabel
                        value="isWorksPaidInFull"
                        control={<Checkbox />}
                        label="Any Works Carried Out Have Been Paid In Full"
                      />
                      <FormControlLabel value="ownerBuilder" control={<Checkbox />} label="Owner Builder" />
                      <FormControlLabel value="isSigned" control={<Checkbox />} label="Construction Contract Signed" />
                    </RadioGroup>
                  </Grid>
                </Grid>
              )}

              {["Other"].includes(allValues?.loanPurpose) && (
                <Grid container item spacing={1} my={1}>
                  <Grid item sm={12}>
                    <TextField
                      fullWidth
                      id="otherDetails"
                      label="Other Details"
                      variant="filled"
                      type="text"
                      size="small"
                      name="otherDetails"
                      disabled={isDisableEdit}
                      value={allValues?.otherDetails}
                      onChange={(event) => handle.onChangeField(event?.target?.value, "otherDetails")}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
});

export default LoanDetails;
